/**
 * توابع خالص (بدون وابستگی به Vue) برای ActionForm.
 * فایل: core/components/action/ActionForm/ActionFormUtils.js
 */

/* -------------------------------------------------------------------------- */
/* Constants                                                                  */
/* -------------------------------------------------------------------------- */

export const ACTION_TYPES = Object.freeze({
  FREE_SHIPPING: "free_shipping",
  WALLET_CHARGE: "wallet_charge",
});

export const DATE_FORMAT = "YYYY-MM-DD HH:mm";

const TITLE_MAX_LENGTH = 255;
const PERSIAN_DIGITS = "۰۱۲۳۴۵۶۷۸۹";
const ARABIC_DIGITS = "٠١٢٣٤٥٦٧٨٩";

const isEmpty = (value) =>
  value === null || typeof value === "undefined" || value === "";

/* -------------------------------------------------------------------------- */
/* Amount helpers                                                             */
/* -------------------------------------------------------------------------- */

/** ارقام فارسی و عربی را به انگلیسی تبدیل می‌کند. */
export const toEnglishDigits = (value) =>
  String(value)
    .replace(/[۰-۹]/g, (d) => PERSIAN_DIGITS.indexOf(d))
    .replace(/[٠-٩]/g, (d) => ARABIC_DIGITS.indexOf(d));

/**
 * هر ورودی (تایپ‌شده، فارسی، جداکننده‌دار، یا عدد سرور مثل "150000.00")
 * را به عدد صحیح یا null تبدیل می‌کند.
 */
export const parseAmount = (value) => {
  if (isEmpty(value)) {
    return null;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? Math.trunc(value) : null;
  }

  const normalized = toEnglishDigits(value).trim();

  // مقدار اعشاری سرور مثل "150000.00": فقط بخش صحیح
  if (/^\d+\.\d+$/.test(normalized)) {
    return Math.trunc(Number(normalized));
  }

  const digits = normalized.replace(/\D/g, "");
  return digits === "" ? null : Number(digits);
};

/** 1234567 -> "1,234,567" */
export const formatAmount = (value) => {
  const number = parseAmount(value);

  return number === null
    ? ""
    : String(number).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

/* -------------------------------------------------------------------------- */
/* Date helpers                                                               */
/* -------------------------------------------------------------------------- */

export const parseDate = (value) => {
  const moment = typeof window !== "undefined" ? window.moment : null;

  if (!value || !moment) {
    return null;
  }

  const formats = [
    "YYYY-MM-DD HH:mm",
    "YYYY-MM-DD HH:mm:ss",
    "YYYY.MM.DD HH:mm",
    moment.ISO_8601,
  ];

  const strict = moment(value, formats, true);

  return strict.isValid() ? strict : moment(value);
};

export const normalizeDate = (value) => {
  const date = parseDate(value);

  return date && date.isValid() ? date.format(DATE_FORMAT) : value || "";
};

/* -------------------------------------------------------------------------- */
/* Model                                                                      */
/* -------------------------------------------------------------------------- */

export const createEmptyAction = () => ({
  id: null,
  title: "",
  level_id: null,
  type: ACTION_TYPES.FREE_SHIPPING,
  type_value: "",
  start_date: "",
  end_date: "",
  index: null,
});

/**
 * type_value در مدل فرم همیشه «عدد» یا «رشته خالی» است.
 * - ارسال رایگان: حداقل مبلغ سبد خرید (۰ یعنی بدون حداقل، پس خالی نمایش داده می‌شود)
 * - شارژ کیف پول: مبلغ شارژ
 */
const normalizeTypeValue = (type, typeValue) => {
  const amount = parseAmount(typeValue);

  if (amount === null) {
    return "";
  }

  if (type === ACTION_TYPES.FREE_SHIPPING && amount === 0) {
    return "";
  }

  return amount;
};

export const normalizeAction = (source) => {
  const value = source || {};
  const type = value.type || ACTION_TYPES.FREE_SHIPPING;

  return {
    ...createEmptyAction(),
    ...value,
    level_id:
      value.level_id || (value.level && value.level.id ? value.level.id : null),
    type,
    type_value: normalizeTypeValue(type, value.type_value),
  };
};

/* -------------------------------------------------------------------------- */
/* Validation                                                                 */
/* -------------------------------------------------------------------------- */

const validateTitle = (action) => {
  const title = String(action.title || "").trim();

  if (!title) {
    return "عنوان عملیات را وارد کنید.";
  }

  if (title.length > TITLE_MAX_LENGTH) {
    return "عنوان عملیات نباید بیشتر از ۲۵۵ کاراکتر باشد.";
  }

  return null;
};

const validateLevel = (action) =>
  isEmpty(action.level_id) ? "یک سطح را انتخاب کنید." : null;

const validateType = (action) =>
  Object.values(ACTION_TYPES).includes(action.type)
    ? null
    : "نوع عملیات معتبر نیست.";

const validateTypeValue = (action) => {
  const value = action.type_value;

  if (action.type === ACTION_TYPES.WALLET_CHARGE) {
    if (isEmpty(value)) {
      return "مبلغ شارژ کیف پول را وارد کنید.";
    }

    if (Number.isNaN(Number(value))) {
      return "مبلغ شارژ کیف پول باید عدد باشد.";
    }

    if (Number(value) < 0) {
      return "مبلغ شارژ کیف پول نمی‌تواند منفی باشد.";
    }

    return null;
  }

  // ارسال رایگان: اختیاری، ولی اگر پر شد باید معتبر باشد
  if (isEmpty(value)) {
    return null;
  }

  return Number.isFinite(Number(value)) && Number(value) >= 0
    ? null
    : "حداقل مبلغ سبد خرید باید عددی معتبر باشد.";
};

const validateDates = (action) => {
  const errors = {};

  if (!action.start_date) {
    errors.start_date = "تاریخ شروع را انتخاب کنید.";
  }

  if (!action.end_date) {
    errors.end_date = "تاریخ پایان را انتخاب کنید.";
  }

  if (!action.start_date || !action.end_date) {
    return errors;
  }

  const start = parseDate(action.start_date);
  const end = parseDate(action.end_date);
  const startValid = Boolean(start && start.isValid());
  const endValid = Boolean(end && end.isValid());

  if (!startValid) {
    errors.start_date = "تاریخ شروع معتبر نیست.";
  }

  if (!endValid) {
    errors.end_date = "تاریخ پایان معتبر نیست.";
  }

  if (startValid && endValid && end.isBefore(start)) {
    errors.end_date = "تاریخ پایان باید برابر یا بعد از تاریخ شروع باشد.";
  }

  return errors;
};

const FIELD_VALIDATORS = {
  title: validateTitle,
  level_id: validateLevel,
  type: validateType,
  type_value: validateTypeValue,
};

/** خروجی: آبجکت خطاها (خالی = فرم معتبر). */
export const validateAction = (action) => {
  const errors = {};

  Object.keys(FIELD_VALIDATORS).forEach((field) => {
    const message = FIELD_VALIDATORS[field](action);

    if (message) {
      errors[field] = message;
    }
  });

  return { ...errors, ...validateDates(action) };
};

/* -------------------------------------------------------------------------- */
/* API payload / response                                                     */
/* -------------------------------------------------------------------------- */

/** مقدار type_value که به API ارسال می‌شود (همیشه عدد؛ خالی = ۰). */
export const resolveTypeValueForApi = (action) => {
  const amount = parseAmount(action.type_value);

  return amount === null ? 0 : amount;
};

export const buildActionFormData = (action, mode) => {
  const fd = new FormData();

  fd.append("title", String(action.title || "").trim());
  fd.append("level_id", action.level_id);
  fd.append("type", action.type);
  fd.append("type_value", resolveTypeValueForApi(action));
  fd.append("start_date", normalizeDate(action.start_date));
  fd.append("end_date", normalizeDate(action.end_date));

  if (mode === "edit") {
    fd.append("_method", "PUT");
  }

  return fd;
};

export const extractSavedAction = (response) => {
  const body = response && response.data ? response.data : {};

  if (body.data && body.data.action) {
    return body.data.action;
  }

  if (body.action) {
    return body.action;
  }

  if (body.data && typeof body.data === "object") {
    return body.data;
  }

  return null;
};

export const attachLevel = (action, savedAction, levels) => {
  const result = {
    ...action,
    ...(savedAction || {}),
    level_id: (savedAction && savedAction.level_id) || action.level_id,
  };

  if (!result.level) {
    result.level = levels.find((level) => level.id == result.level_id);
  }

  return result;
};

/** خطاهای validation لاراول را به { field: firstMessage } تبدیل می‌کند. */
export const mapServerErrors = (error) => {
  const serverErrors =
    error && error.response && error.response.data
      ? error.response.data.errors
      : null;

  if (!serverErrors || typeof serverErrors !== "object") {
    return {};
  }

  return Object.keys(serverErrors).reduce((mapped, field) => {
    const messages = serverErrors[field];

    mapped[field] = Array.isArray(messages) ? messages[0] : String(messages);

    return mapped;
  }, {});
};

export const buildSuccessMessage = (response, mode) => {
  if (response && response.data && response.data.message) {
    return response.data.message;
  }

  return mode === "edit"
    ? "عملیات با موفقیت ویرایش شد."
    : "عملیات با موفقیت ثبت شد.";
};